import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { ActionButtonSizes } from 'components/action-button';
import CloseButton from 'components/close-button';
import { ItemThumbnailWithBadge } from 'components/item-thumbnail-with-badge/item-thumbnail-with-badge';
import { DeliveryProgress } from 'components/order-status/components/delivery-progress/delivery-progress';
import { useOrderStatusDetail } from 'components/order-status/use-order-status-details';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { primitive } from 'styles/constants/primitives';
import { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

import {
  ActionButtonStyled,
  Card,
  CardContainer,
  LeftContainer,
  OrderNumber,
  RightContainer,
  ServiceModeContainer,
  ServiceModeLabel,
} from './styled';

export const OrderStatusCard = () => {
  const { variant } = useExperiments();
  const { formatMessage } = useIntl();
  const { linkTo } = useNavigation();
  const { logEvent } = useMParticleContext();
  const orderStatusDetail = useOrderStatusDetail();
  // we need this to save the state until we get a new order placed
  const [closedState, setClosedState, clearClosedStatus] = useLocalStorageState<boolean>({
    key: StorageKeys.ORDER_STATUS_CARD_CLOSED,
    defaultReturnValue: false,
  });

  const isAvailable = () => variant(RunningExperiments.LiveOrderStatusTracker, 'treatment');

  useEffectOnUnmount(() => {
    if (!orderStatusDetail) {
      clearClosedStatus();
    }
  });

  if (!orderStatusDetail || !isAvailable() || closedState) {
    return null;
  }

  const {
    orderNumber,
    icon,
    heading,
    details,
    deliveryStatus,
    image,
    name,
    totalItems,
    orderId,
  } = orderStatusDetail;

  // @ts-ignore
  const imageHighlighted = JSON.parse(image);

  return (
    <CardContainer>
      <Card>
        <LeftContainer>
          <Box w="$9">
            <CloseButton backgroundColor={primitive.$white} onPress={() => setClosedState(true)} />
          </Box>
          <OrderNumber>
            {orderNumber && formatMessage({ id: 'orderConfirmationNumber' }, { orderNumber })}
          </OrderNumber>
          <ServiceModeContainer>
            {icon}
            <ServiceModeLabel>{heading}</ServiceModeLabel>
          </ServiceModeContainer>
          {details}
          {deliveryStatus && (
            <Box w="5/6">
              <DeliveryProgress deliveryStatus={deliveryStatus} />
            </Box>
          )}
        </LeftContainer>
        <RightContainer>
          {image && (
            <ItemThumbnailWithBadge
              item={{ image: imageHighlighted, name: name || '' }}
              totalItems={totalItems}
            />
          )}
          <ActionButtonStyled
            onPress={() => {
              logEvent(CustomEventNames.ORDER_STATUS_CARD_ORDER_DETAIL_CLICK, EventTypes.Other);
              linkTo(`${routes.orderConfirmation}/${orderId}`);
            }}
            size={ActionButtonSizes.SMALL}
          >
            {formatMessage({ id: 'orderDetailsHeading' })}
          </ActionButtonStyled>
        </RightContainer>
      </Card>
    </CardContainer>
  );
};
